import React, { useCallback, useEffect, useState } from 'react';

import {
  ApplicationDetail,
  PresignedDocumentUpload,
  TeacherApplicationProfile,
  TeacherDocumentType,
  confirmDocument,
  getMyApplication,
  presignDocument,
  submitMyApplication,
  upsertMyApplication,
} from '../api/teacher-applications';
import VerificationSteps from '../components/VerificationSteps';

const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

/**
 * Send the file where the presign said, the way it said.
 *
 * In every deployed stage this is a presigned S3 POST (CTO decision 12 Oct
 * 2026): the form fields carry a signed policy, and S3 refuses a file over the
 * size limit or of a different type than the one presigned (TCH-04-FR-03). The
 * file must be the last form field — S3 ignores anything after it. The local
 * stub still answers to PUT.
 */
export function sendToStorage(presigned: PresignedDocumentUpload, file: File): Promise<Response> {
  if (presigned.uploadMethod === 'POST') {
    const form = new FormData();
    Object.entries(presigned.uploadFields ?? {}).forEach(([name, value]) =>
      form.append(name, value),
    );
    form.append('file', file);
    return fetch(presigned.uploadUrl, { method: 'POST', body: form });
  }
  return fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
}

/**
 * TCH-04-FR-04 — what a reviewer needs to be able to read, said before the
 * teacher photographs anything rather than after a rejection.
 */
const CAPTURE_GUIDANCE = [
  'El texto debe leerse completo, sin partes borrosas.',
  'El documento completo debe verse: las cuatro esquinas dentro de la foto.',
  'Evita reflejos y sombras encima del documento.',
];

const REQUIRED_DOCS: TeacherDocumentType[] = ['ID', 'CREDENTIAL'];
const OPTIONAL_DOCS: TeacherDocumentType[] = ['REFERENCE'];
const DOC_LABELS: Record<TeacherDocumentType, string> = {
  ID: 'Identificación oficial',
  CREDENTIAL: 'Credencial / certificación',
  REFERENCE: 'Carta de referencia (opcional)',
};

const emptyProfile = (): TeacherApplicationProfile => ({
  languages: [],
  yearsExperience: 0,
  certifications: [],
  methodology: '',
  subjectCoverage: [],
  levelCoverage: [],
  headlineHourlyRate: 0,
  bio: '',
  legalName: '',
  phoneCountryCode: '52',
  phoneNumber: '',
  country: 'MX',
  timezone: 'America/Mexico_City',
  ageRangesServed: [],
  emailCommunicationPreference: 'ALL',
  marketingOptIn: false,
  referralSource: '',
});

/** The countries IQMaxx operates in today. Adding one is a business decision. */
const COUNTRIES = [
  { code: 'MX', label: 'México' },
  { code: 'CO', label: 'Colombia' },
  { code: 'AR', label: 'Argentina' },
  { code: 'CL', label: 'Chile' },
  { code: 'PE', label: 'Perú' },
  { code: 'US', label: 'Estados Unidos' },
];

/** Same vocabulary the availability editor uses, so the two cannot disagree. */
const TIMEZONES = [
  'America/Mexico_City',
  'America/Bogota',
  'America/Argentina/Buenos_Aires',
  'America/Santiago',
  'America/Lima',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

/** The ADR's own bracketing of the 6–14 audience IQMax serves. */
const AGE_RANGES = ['6-8', '9-11', '12-14'];

const stateBadgeStyle = (state: string): React.CSSProperties => {
  const palette: Record<string, { bg: string; fg: string }> = {
    DRAFT: { bg: '#e9ecef', fg: '#495057' },
    SUBMITTED: { bg: '#d0ebff', fg: '#1864ab' },
    UNDER_REVIEW: { bg: '#fff3bf', fg: '#5c3c00' },
    APPROVED: { bg: '#d3f9d8', fg: '#2b8a3e' },
    REJECTED: { bg: '#ffe3e3', fg: '#c92a2a' },
    NEEDS_RESUBMIT: { bg: '#ffe8cc', fg: '#d9480f' },
    AWAITING_UPLOAD: { bg: '#ffe8cc', fg: '#d9480f' },
    PENDING: { bg: '#d0ebff', fg: '#1864ab' },
    ACCEPTED: { bg: '#d3f9d8', fg: '#2b8a3e' },
  };
  const c = palette[state] || palette.DRAFT;
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    background: c.bg,
    color: c.fg,
    fontSize: 12,
    fontWeight: 600,
  };
};

const TeacherOnboarding: React.FC = () => {
  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [profile, setProfile] = useState<TeacherApplicationProfile>(emptyProfile());
  const [savingProfile, setSavingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingType, setUploadingType] = useState<TeacherDocumentType | null>(null);

  const refresh = useCallback(async () => {
    try {
      const d = await getMyApplication();
      setDetail(d);
      setProfile(d.application.profile);
      setError(null);
    } catch (e: any) {
      const msg = e?.message || '';
      if (msg.includes('No application')) {
        setDetail(null);
      } else {
        setError(msg || 'Error cargando aplicación');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isLocked =
    detail !== null &&
    detail.application.state !== 'DRAFT' &&
    detail.application.state !== 'NEEDS_RESUBMIT';

  const canSubmit =
    detail !== null &&
    (detail.application.state === 'DRAFT' || detail.application.state === 'NEEDS_RESUBMIT') &&
    detail.missingRequiredDocuments.length === 0 &&
    !detail.documents.some((d) => d.status === 'REJECTED');

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError(null);
    setInfo(null);
    try {
      await upsertMyApplication(profile);
      setInfo('Borrador guardado.');
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar el perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!detail) return;
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      await submitMyApplication();
      setInfo('Aplicación enviada para revisión.');
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'No se pudo enviar la aplicación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadFile = async (documentType: TeacherDocumentType, file: File): Promise<void> => {
    if (!detail) return;

    // TCH-04-FR-03. Checked here as well as server-side because the server sees
    // the declared content type but never the bytes: nothing downstream can
    // tell the teacher their file was too large.
    if (file.size > MAX_DOCUMENT_BYTES) {
      setError(`${file.name} pesa ${(file.size / 1024 / 1024).toFixed(1)} MB. El máximo es 10 MB.`);
      return;
    }
    if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
      setError(`${file.name} no es un formato aceptado. Usa PDF, PNG o JPG.`);
      return;
    }

    setUploadingType(documentType);
    setError(null);
    setInfo(null);
    try {
      const presigned = await presignDocument(detail.application.applicationId, {
        documentType,
        contentType: file.type,
        fileName: file.name,
      });

      // The upload itself decides whether this document exists. It used to be
      // wrapped in a try/catch that swallowed the failure and confirmed anyway,
      // so a teacher saw "Documento subido", a reviewer saw a document row, and
      // there was no file anywhere — on a screen that decides who teaches
      // children. A failure here leaves the row AWAITING_UPLOAD, which does not
      // count towards the required set, and the teacher is told to retry.
      const response = await sendToStorage(presigned, file);
      if (!response.ok) {
        throw new Error(`El archivo no se pudo guardar (${response.status}).`);
      }

      await confirmDocument(detail.application.applicationId, {
        documentId: presigned.documentId,
        s3Key: presigned.s3Key,
      });
      setInfo(`Documento ${DOC_LABELS[documentType]} subido.`);
      await refresh();
    } catch (e: any) {
      const message = `No se pudo subir ${DOC_LABELS[documentType]}: ${
        e?.message || 'error de red'
      }. Vuelve a intentarlo.`;
      // Refresh first, then report. The row stays AWAITING_UPLOAD and the screen
      // needs to show that — but `refresh` clears the error banner on success,
      // so setting the message before it would erase the reason the teacher
      // needs to read.
      await refresh().catch(() => undefined);
      setError(message);
    } finally {
      setUploadingType(null);
    }
  };

  if (loading) return <p>Cargando…</p>;

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: 16 }}>
      <h1 style={{ marginBottom: 4 }}>Aplicación de profesor</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Completa tu perfil y sube los documentos requeridos. Una vez aprobado podrás publicar
        cursos.
      </p>

      {detail && (
        <div style={{ marginBottom: 16 }}>
          Estado actual:{' '}
          <span style={stateBadgeStyle(detail.application.state)}>{detail.application.state}</span>
          {detail.application.reviewReason && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: '#fff4e6',
                border: '1px solid #ffd8a8',
                borderRadius: 6,
                color: '#5c3c00',
              }}
            >
              {/*
                The API returns the standard explanation for the reason the
                reviewer recorded, never their own words (TCH-04-FR-08), so
                this is no longer "a note from the reviewer" and must not be
                labelled as one.
              */}
              <strong>Qué necesitamos:</strong> {detail.application.reviewReason}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          style={{
            background: '#ffe3e3',
            color: '#c92a2a',
            padding: 12,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}
      {info && (
        <div
          style={{
            background: '#d3f9d8',
            color: '#2b8a3e',
            padding: 12,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          {info}
        </div>
      )}

      {/* TCH-03-FR-02 — identity and contact. Required to submit, not to save:
          verification compares a document against a name, support needs a
          number, and scheduling needs to know which timezone the hours are in. */}
      <h2>Identidad y contacto</h2>
      <p style={{ color: '#666', marginTop: 0, fontSize: 14 }}>
        Como aparece en tu identificación oficial. Solo lo ve el equipo de verificación.
      </p>

      <FieldRow label="Nombre legal completo">
        <input
          type="text"
          value={profile.legalName ?? ''}
          onChange={(e) => setProfile({ ...profile, legalName: e.target.value })}
          style={inputStyle}
          disabled={isLocked}
        />
      </FieldRow>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 110 }}>
          <FieldRow label="Código país">
            <input
              type="text"
              inputMode="numeric"
              placeholder="52"
              value={profile.phoneCountryCode ?? ''}
              onChange={(e) => setProfile({ ...profile, phoneCountryCode: e.target.value })}
              style={inputStyle}
              disabled={isLocked}
            />
          </FieldRow>
        </div>
        <div style={{ flex: '1 1 220px' }}>
          <FieldRow label="Teléfono">
            <input
              type="tel"
              value={profile.phoneNumber ?? ''}
              onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
              style={inputStyle}
              disabled={isLocked}
            />
          </FieldRow>
        </div>
      </div>

      <FieldRow label="País">
        <select
          value={profile.country ?? ''}
          onChange={(e) => setProfile({ ...profile, country: e.target.value })}
          style={inputStyle}
          disabled={isLocked}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
      </FieldRow>

      <FieldRow label="Zona horaria">
        <select
          value={profile.timezone ?? ''}
          onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
          style={inputStyle}
          disabled={isLocked}
        >
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </FieldRow>

      <h2>Perfil</h2>
      {/* A fieldset, not a FieldRow: FieldRow renders a <label>, and a label
          wrapping several checkboxes nests labels — invalid markup, and it
          makes each box's accessible name ambiguous. A group of related
          controls takes a legend. */}
      <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
        <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, padding: 0 }}>
          Edades que atiendes
        </legend>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {AGE_RANGES.map((range) => {
            const selected = (profile.ageRangesServed ?? []).includes(range);
            return (
              <label key={range} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={isLocked}
                  onChange={() =>
                    setProfile({
                      ...profile,
                      ageRangesServed: selected
                        ? (profile.ageRangesServed ?? []).filter((r) => r !== range)
                        : [...(profile.ageRangesServed ?? []), range],
                    })
                  }
                />
                {range} años
              </label>
            );
          })}
        </div>
      </fieldset>
      <FieldRow label="Idiomas (separados por coma)">
        <input
          type="text"
          value={profile.languages.join(', ')}
          onChange={(e) =>
            setProfile({
              ...profile,
              languages: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={isLocked}
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Años de experiencia">
        <input
          type="number"
          min={0}
          max={80}
          value={profile.yearsExperience}
          onChange={(e) => setProfile({ ...profile, yearsExperience: Number(e.target.value) })}
          disabled={isLocked}
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Certificaciones (separadas por coma)">
        <input
          type="text"
          value={profile.certifications.join(', ')}
          onChange={(e) =>
            setProfile({
              ...profile,
              certifications: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={isLocked}
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Metodología (mínimo 20 caracteres)">
        <textarea
          value={profile.methodology}
          onChange={(e) => setProfile({ ...profile, methodology: e.target.value })}
          disabled={isLocked}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>
      <FieldRow label="Materias (separadas por coma)">
        <input
          type="text"
          value={profile.subjectCoverage.join(', ')}
          onChange={(e) =>
            setProfile({
              ...profile,
              subjectCoverage: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={isLocked}
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Niveles (separados por coma)">
        <input
          type="text"
          value={profile.levelCoverage.join(', ')}
          onChange={(e) =>
            setProfile({
              ...profile,
              levelCoverage: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          disabled={isLocked}
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Tarifa por hora (USD)">
        <input
          type="number"
          min={0}
          step={0.01}
          value={profile.headlineHourlyRate}
          onChange={(e) => setProfile({ ...profile, headlineHourlyRate: Number(e.target.value) })}
          disabled={isLocked}
          style={inputStyle}
        />
      </FieldRow>
      <FieldRow label="Bio (opcional)">
        <textarea
          value={profile.bio || ''}
          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          disabled={isLocked}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </FieldRow>

      {/* TCH-03-FR-02 — preferences and source. All optional: consent that is
          required is not consent, and nobody should be blocked from teaching for
          declining to say how they heard about us. */}
      <h2 style={{ marginTop: 28 }}>Preferencias</h2>

      <FieldRow label="Correos que quieres recibir">
        <select
          value={profile.emailCommunicationPreference ?? 'ALL'}
          onChange={(e) =>
            setProfile({
              ...profile,
              emailCommunicationPreference: e.target.value as 'ALL' | 'ESSENTIAL_ONLY',
            })
          }
          style={inputStyle}
          disabled={isLocked}
        >
          <option value="ALL">Todo: avisos de sesiones, pagos y novedades</option>
          <option value="ESSENTIAL_ONLY">Solo lo indispensable: sesiones y pagos</option>
        </select>
      </FieldRow>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={profile.marketingOptIn ?? false}
          disabled={isLocked}
          onChange={(e) => setProfile({ ...profile, marketingOptIn: e.target.checked })}
        />
        <span style={{ fontSize: 14 }}>
          Quiero recibir novedades y consejos para profesores (opcional)
        </span>
      </label>

      <FieldRow label="¿Cómo supiste de IQMaxx? (opcional)">
        <input
          type="text"
          value={profile.referralSource ?? ''}
          onChange={(e) => setProfile({ ...profile, referralSource: e.target.value })}
          style={inputStyle}
          disabled={isLocked}
        />
      </FieldRow>

      {!isLocked && (
        <button
          onClick={handleSaveProfile}
          disabled={savingProfile}
          style={primaryBtn(savingProfile)}
        >
          {savingProfile ? 'Guardando…' : 'Guardar perfil'}
        </button>
      )}

      <h2 style={{ marginTop: 32 }}>Documentos</h2>
      {/* TCH-04-FR-01: why this is being asked, before it is asked. */}
      <p style={{ color: '#666', marginTop: 0 }}>
        Verificamos la identidad y las credenciales de cada profesor antes de que pueda dar clase.
        Las familias confían menores de edad a quien enseña en IQMaxx, y esta revisión es lo que
        respalda esa confianza. Tus documentos se guardan cifrados, los revisa únicamente el equipo
        de verificación y no se comparten con las familias ni aparecen en tu perfil público.
      </p>
      <p style={{ color: '#666' }}>
        Identificación y credencial son obligatorias. PDF, PNG o JPG, máximo 10 MB.
      </p>
      <ul style={{ color: '#666', fontSize: 14, marginTop: 0, paddingLeft: 20 }}>
        {CAPTURE_GUIDANCE.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {[...REQUIRED_DOCS, ...OPTIONAL_DOCS].map((type) => {
        const existing = (detail?.documents || []).find((d) => d.type === type);
        return (
          <DocumentSlot
            key={type}
            type={type}
            label={DOC_LABELS[type]}
            existing={existing}
            disabled={isLocked || uploadingType !== null}
            onSelect={(file) => handleUploadFile(type, file)}
          />
        );
      })}

      <h2 style={{ marginTop: 32 }}>Enviar para revisión</h2>
      {detail && detail.missingRequiredDocuments.length > 0 && (
        <p style={{ color: '#c92a2a' }}>
          Faltan documentos: {detail.missingRequiredDocuments.join(', ')}
        </p>
      )}
      {detail && detail.documents.some((d) => d.status === 'REJECTED') && (
        <p style={{ color: '#c92a2a' }}>
          Hay documentos rechazados que debes reemplazar antes de reenviar.
        </p>
      )}
      <button
        onClick={handleSubmitApplication}
        disabled={!canSubmit || submitting}
        style={primaryBtn(!canSubmit || submitting)}
      >
        {submitting
          ? 'Enviando…'
          : detail?.application.state === 'NEEDS_RESUBMIT'
          ? 'Reenviar aplicación'
          : 'Enviar aplicación'}
      </button>

      {/* TCH-04-FR-05 / TCH-03-FR-08 — UC-T03's identity steps. Once an
          application exists the country of residence is known, which the
          background check needs; they can run in parallel with review (T03-D01). */}
      {detail && <VerificationSteps />}
    </div>
  );
};

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
}
/**
 * The control lives *inside* the label (S11.2).
 *
 * It used to be a sibling, so the eight fields of the longest form in the
 * product had a visible caption and no programmatic label at all: a screen
 * reader announced "edit text, blank", and clicking the caption did not focus
 * the field. Wrapping associates them implicitly, which works for any child
 * without threading generated ids through every call site.
 */
const FieldRow: React.FC<FieldRowProps> = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 12 }}>
    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
      {label}
    </span>
    {children}
  </label>
);

interface DocumentSlotProps {
  type: TeacherDocumentType;
  label: string;
  existing?: import('../api/teacher-applications').TeacherDocument;
  disabled: boolean;
  onSelect: (file: File) => void;
}
const DocumentSlot: React.FC<DocumentSlotProps> = ({
  type,
  label,
  existing,
  disabled,
  onSelect,
}) => (
  <div
    style={{
      border: '1px solid #e9ecef',
      borderRadius: 6,
      padding: 12,
      marginBottom: 12,
      background:
        existing?.status === 'REJECTED'
          ? '#fff5f5'
          : existing?.status === 'AWAITING_UPLOAD'
          ? '#fff8e6'
          : '#fff',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <strong>{label}</strong>
      {existing && (
        <span style={stateBadgeStyle(existing.status)}>
          {existing.status === 'AWAITING_UPLOAD' ? 'SIN SUBIR' : existing.status}
        </span>
      )}
    </div>
    {existing && existing.status === 'AWAITING_UPLOAD' && (
      <div style={{ color: '#d9480f', fontSize: 13, marginTop: 4 }}>
        El archivo no llegó a subirse. Vuelve a elegirlo — este documento aún no cuenta como
        entregado.
      </div>
    )}
    {existing && existing.status !== 'AWAITING_UPLOAD' && (
      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
        Subido: {new Date(existing.uploadedAt).toLocaleString()}
        {existing.fileName && <> — {existing.fileName}</>}
      </div>
    )}
    {existing?.scanStatus === 'THREATS_FOUND' && (
      <div role="alert" style={{ color: '#c92a2a', fontSize: 13, marginTop: 4 }}>
        El análisis de seguridad detectó un problema en este archivo y nadie podrá abrirlo. Sube una
        copia nueva desde un equipo de confianza — este documento no cuenta como entregado.
      </div>
    )}
    {existing?.scanStatus === 'PENDING' && (
      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
        Analizando el archivo en busca de software malicioso…
      </div>
    )}
    {existing?.rejectionReason && (
      <div style={{ color: '#c92a2a', fontSize: 13, marginTop: 4 }}>
        Qué necesitamos: {existing.rejectionReason}
      </div>
    )}
    {!disabled && (
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.target.value = '';
        }}
        style={{ marginTop: 8 }}
        aria-label={`Subir ${label}`}
        data-testid={`upload-${type}`}
      />
    )}
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 8,
  border: '1px solid #ced4da',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  padding: '10px 20px',
  border: 'none',
  borderRadius: 6,
  background: disabled ? '#a0c4e8' : '#1971c2',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default TeacherOnboarding;
